/**
 * Profile Page — editorial layout with hairline sections
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { BellIcon, BellSlashIcon } from '@heroicons/react/24/outline';
import PostCard from './feed/PostCard';
import AuraAssistant from './AuraAssistant';
import { PortraitFrame } from './common/editorial/EditorialControls';
import UserAvatar from './common/UserAvatar';

const ProfilePage = ({ user: currentUser }) => {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts');
  const [isFollowing, setIsFollowing] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [loadingFollow, setLoadingFollow] = useState(false);
  const [editingAbout, setEditingAbout] = useState(false);
  const [editForm, setEditForm] = useState({
    bio: '',
    interests: [],
    expertise: [],
    newInterest: '',
    newExpertise: '',
  });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (username) loadProfile();
    else if (currentUser) loadOwnProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, currentUser]);

  const loadOwnProfile = async () => {
    try {
      setLoading(true);
      const userProfileRes = await axios.get('/api/user/profile');
      const userProfile = userProfileRes.data;

      if (!userProfile?.user_id) {
        setLoading(false);
        return;
      }

      try {
        const profileRes = await axios.get(`/api/platform/profile/by-id/${userProfile.user_id}`);
        const profileData = profileRes.data.data;

        if (profileData) {
          setProfile(profileData);
          setIsFollowing(false);
          setNotificationsEnabled(false);

          try {
            const postsRes = await axios.get('/api/platform/posts', {
              params: { entity_type: 'user', entity_id: profileData.user_id },
            });
            setPosts(postsRes.data.data?.posts || []);
          } catch {
            setPosts([]);
          }
        } else {
          createBasicProfile(userProfile);
        }
      } catch {
        createBasicProfile(userProfile);
      }
    } catch (err) {
      console.error('Error loading own profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const createBasicProfile = (userProfile) => {
    setProfile({
      user_id: userProfile.user_id,
      username: userProfile.email?.split('@')[0] || 'user',
      display_name:
        userProfile.full_name ||
        userProfile.profile_name ||
        userProfile.email?.split('@')[0] ||
        'User',
      avatar_url: userProfile.avatar_url,
      bio: null,
      posts_count: 0,
      followers_count: 0,
      following_count: 0,
      is_following: false,
      notifications_enabled: false,
      is_verified_advisor: false,
    });
    setPosts([]);
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      const profileRes = await axios.get(`/api/platform/profile/${username}`);
      const profileData = profileRes.data.data;
      if (!profileData) {
        setLoading(false);
        return;
      }

      setProfile(profileData);
      setIsFollowing(profileData.is_following || false);
      setNotificationsEnabled(profileData.notifications_enabled || false);

      try {
        const postsRes = await axios.get('/api/platform/posts', {
          params: { entity_type: 'user', entity_id: profileData.user_id },
        });
        setPosts(postsRes.data.data?.posts || []);
      } catch {
        setPosts([]);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    try {
      setLoadingFollow(true);
      if (isFollowing) {
        await axios.delete(`/api/platform/profile/${username}/follow`);
        setIsFollowing(false);
      } else {
        await axios.post(`/api/platform/profile/${username}/follow`, null, {
          params: { notifications_enabled: notificationsEnabled },
        });
        setIsFollowing(true);
      }
      loadProfile();
    } catch (err) {
      console.error('Error following/unfollowing:', err);
    } finally {
      setLoadingFollow(false);
    }
  };

  const toggleNotifications = async () => {
    try {
      await axios.put(`/api/platform/profile/${username}/follow`, null, {
        params: { enabled: !notificationsEnabled },
      });
      setNotificationsEnabled(!notificationsEnabled);
    } catch (err) {
      console.error('Error toggling notifications:', err);
    }
  };

  const startEditingAbout = () => {
    setEditForm({
      bio: profile.bio || '',
      interests: Array.isArray(profile.interests) ? [...profile.interests] : [],
      expertise: Array.isArray(profile.expertise) ? [...profile.expertise] : [],
      newInterest: '',
      newExpertise: '',
    });
    setEditingAbout(true);
  };

  const saveProfileAbout = async () => {
    if (!currentUser) return;
    try {
      setSavingProfile(true);
      await axios.put('/api/platform/profile', {
        bio: editForm.bio || '',
        interests: editForm.interests || [],
        expertise: editForm.expertise || [],
      });
      setEditingAbout(false);
      if (username) loadProfile();
      else loadOwnProfile();
    } catch (err) {
      console.error('Error saving profile:', err);
    } finally {
      setSavingProfile(false);
    }
  };

  const addArrayItem = (field, value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return;
    setEditForm((prev) => ({
      ...prev,
      [field]: [...(prev[field] || []), trimmed],
    }));
  };

  const removeArrayItem = (field, index) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index),
    }));
  };

  const isOwnProfile = currentUser && profile && currentUser.user_id === profile.user_id;

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <p className="text-body text-secondary">Loading profile…</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <p className="text-body text-secondary">Profile not found</p>
      </div>
    );
  }

  const initials = profile.display_name?.charAt(0)?.toUpperCase() || 'U';

  return (
    <div className="min-h-screen bg-bone pb-20">
      <AuraAssistant screenName="profile" user={currentUser} />

      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Hero */}
        <header className="pb-8 mb-8 border-b border-hairline">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div className="flex items-start gap-5">
              {profile.is_verified_advisor ? (
                <PortraitFrame
                  size="lg"
                  src={profile.avatar_url}
                  alt={profile.display_name}
                  initials={initials}
                />
              ) : (
                <UserAvatar
                  userId={profile.user_id}
                  src={profile.avatar_url}
                  alt={profile.display_name}
                  size="lg"
                  rounded="lg"
                />
              )}
              <div>
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h1 className="font-display text-h1 text-primary">{profile.display_name}</h1>
                  {profile.is_verified_advisor && (
                    <span className="badge-mark badge-mark-earned badge-mark-brass">
                      Verified advisor
                    </span>
                  )}
                </div>
                <p className="text-label text-secondary mb-3">@{profile.username}</p>
                {profile.bio && (
                  <p className="text-body text-primary max-w-prose mb-3">{profile.bio}</p>
                )}
                {profile.interests?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {profile.interests.map((interest, idx) => (
                      <span key={idx} className="topic-tag">
                        {interest}
                      </span>
                    ))}
                  </div>
                )}
                <dl className="flex items-center gap-8 mt-4">
                  <div>
                    <dt className="text-label text-secondary">Posts</dt>
                    <dd className="figure font-display text-h3 text-primary tabular-nums">
                      {profile.posts_count || 0}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-label text-secondary">Followers</dt>
                    <dd className="figure font-display text-h3 text-primary tabular-nums">
                      {profile.followers_count || 0}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-label text-secondary">Following</dt>
                    <dd className="figure font-display text-h3 text-primary tabular-nums">
                      {profile.following_count || 0}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {!isOwnProfile && (
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleFollow}
                  disabled={loadingFollow}
                  className={isFollowing ? 'btn-secondary' : 'btn-primary'}
                >
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
                {isFollowing && (
                  <button
                    type="button"
                    onClick={toggleNotifications}
                    className="btn-ghost"
                    title={notificationsEnabled ? 'Notifications on' : 'Notifications off'}
                    aria-label={notificationsEnabled ? 'Notifications on' : 'Notifications off'}
                  >
                    {notificationsEnabled ? (
                      <BellIcon className="w-5 h-5 text-pine" />
                    ) : (
                      <BellSlashIcon className="w-5 h-5" />
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </header>

        {/* Tabs */}
        <nav className="flex gap-6 border-b border-hairline mb-8">
          {['posts', 'about'].map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`pb-3 text-body font-medium capitalize transition-colors duration-300 ${
                activeTab === tab ? 'nav-tab-active' : 'nav-tab'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>

        {activeTab === 'posts' && (
          <div className="space-y-8">
            {posts.length === 0 ? (
              <p className="text-center text-body text-secondary py-12">No posts yet</p>
            ) : (
              posts.map((post) => (
                <PostCard
                  key={post.post_id}
                  post={post}
                  user={currentUser}
                  onUpdate={() => {}}
                  onDelete={() => {}}
                />
              ))
            )}
          </div>
        )}

        {activeTab === 'about' && (
          <div className="hairline-list space-y-0">
            {(profile.is_verified_advisor || profile.advisor_mode) && (
              <section className="py-6">
                <h3 className="font-display text-h2 text-primary mb-3">Badges</h3>
                <div className="flex flex-wrap gap-2">
                  {profile.is_verified_advisor && (
                    <span className="badge-mark badge-mark-earned badge-mark-brass">
                      Verified advisor
                    </span>
                  )}
                  {profile.advisor_mode && !profile.is_verified_advisor && (
                    <span className="badge-mark">Advisor</span>
                  )}
                </div>
              </section>
            )}

            {editingAbout && isOwnProfile ? (
              <section className="py-6 space-y-6">
                <div>
                  <label className="text-label text-secondary block mb-2">Bio</label>
                  <textarea
                    value={editForm.bio}
                    onChange={(e) => setEditForm((f) => ({ ...f, bio: e.target.value }))}
                    placeholder="Tell others about yourself…"
                    className="input-editorial resize-none min-h-[100px]"
                    rows={4}
                  />
                </div>
                <div>
                  <label className="text-label text-secondary block mb-2">Interests</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(editForm.interests || []).map((item, idx) => (
                      <span key={idx} className="topic-tag flex items-center gap-1">
                        {item}
                        <button
                          type="button"
                          onClick={() => removeArrayItem('interests', idx)}
                          className="text-faint hover:text-primary"
                          aria-label="Remove"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={editForm.newInterest || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f, newInterest: e.target.value }))}
                      placeholder="Add interest"
                      className="input-editorial flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addArrayItem('interests', editForm.newInterest);
                          setEditForm((f) => ({ ...f, newInterest: '' }));
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        addArrayItem('interests', editForm.newInterest);
                        setEditForm((f) => ({ ...f, newInterest: '' }));
                      }}
                      className="btn-secondary"
                    >
                      Add
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-label text-secondary block mb-2">Expertise</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(editForm.expertise || []).map((item, idx) => (
                      <span key={idx} className="topic-tag flex items-center gap-1">
                        {item}
                        <button
                          type="button"
                          onClick={() => removeArrayItem('expertise', idx)}
                          className="text-faint hover:text-primary"
                          aria-label="Remove"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={editForm.newExpertise || ''}
                      onChange={(e) => setEditForm((f) => ({ ...f, newExpertise: e.target.value }))}
                      placeholder="Add expertise"
                      className="input-editorial flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addArrayItem('expertise', editForm.newExpertise);
                          setEditForm((f) => ({ ...f, newExpertise: '' }));
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        addArrayItem('expertise', editForm.newExpertise);
                        setEditForm((f) => ({ ...f, newExpertise: '' }));
                      }}
                      className="btn-secondary"
                    >
                      Add
                    </button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={saveProfileAbout}
                    disabled={savingProfile}
                    className="btn-primary"
                  >
                    {savingProfile ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" onClick={() => setEditingAbout(false)} className="btn-ghost">
                    Cancel
                  </button>
                </div>
              </section>
            ) : (
              <>
                <section className="py-6">
                  <h3 className="font-display text-h2 text-primary mb-2">Bio</h3>
                  {profile.bio ? (
                    <p className="text-body text-primary whitespace-pre-wrap">{profile.bio}</p>
                  ) : (
                    <p className="text-body text-secondary italic">
                      {isOwnProfile ? "You haven't added a bio yet." : 'No bio yet.'}
                    </p>
                  )}
                </section>
                <section className="py-6">
                  <h3 className="font-display text-h2 text-primary mb-2">Interests</h3>
                  {profile.interests?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {profile.interests.map((interest, idx) => (
                        <span key={idx} className="topic-tag">
                          {interest}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-body text-secondary italic">
                      {isOwnProfile
                        ? 'Add interests so others can connect with you.'
                        : 'No interests listed.'}
                    </p>
                  )}
                </section>
                <section className="py-6">
                  <h3 className="font-display text-h2 text-primary mb-2">Expertise</h3>
                  {profile.expertise?.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {profile.expertise.map((exp, idx) => (
                        <span key={idx} className="topic-tag">
                          {exp}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-body text-secondary italic">
                      {isOwnProfile
                        ? 'List your expertise (e.g. retirement, tax).'
                        : 'No expertise listed.'}
                    </p>
                  )}
                </section>
                {profile.advisor_mode && profile.advisor_bio && (
                  <section className="py-6">
                    <h3 className="font-display text-h2 text-primary mb-2">Advisor bio</h3>
                    <p className="text-body text-primary">{profile.advisor_bio}</p>
                  </section>
                )}
                {isOwnProfile && (
                  <section className="py-6">
                    <button type="button" onClick={startEditingAbout} className="btn-secondary">
                      Edit profile
                    </button>
                  </section>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
